import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { format } from "date-fns";

interface UserAccessLog {
  log_id: string;
  user_id?: string;
  username?: string;
  email?: string;
  action: string;
  endpoint?: string;
  method?: string;
  ip_address?: string;
  user_agent?: string;
  status_code?: number;
  response_time_ms?: number;
  details?: string;
  timestamp: string;
  success: boolean;
}

interface LogsSummary {
  summary: {
    total_actions: number;
    successful_actions: number;
    failed_actions: number;
    success_rate: number;
  };
  action_breakdown: Record<string, number>;
  recent_logs: UserAccessLog[];
}

const AdminLogs: React.FC = () => {
  const [logs, setLogs] = useState<UserAccessLog[]>([]);
  const [summary, setSummary] = useState<LogsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [username, setUsername] = useState("");
  const [action, setAction] = useState("");
  const [limit, setLimit] = useState(100);

  const fetchLogsSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/admin/logs/summary");
      setSummary(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch logs summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (username) params.append("username", username);
      if (action) params.append("action", action);
      params.append("limit", limit.toString());

      const response = await api.get(`/api/admin/user-access-logs?${params}`);
      setLogs(response.data.logs);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  }, [username, action, limit]);

  // Fetch data on component mount
  useEffect(() => {
    fetchLogsSummary();
    fetchLogs();
  }, [fetchLogs]);

  const handleFilter = () => {
    fetchLogs();
  };

  const handleClearFilters = () => {
    setUsername("");
    setAction("");
    fetchLogs();
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss");
    } catch {
      return timestamp;
    }
  };

  const getActionColor = (action: string) => {
    if (action.includes("login")) return "text-green-600";
    if (action.includes("logout")) return "text-blue-600";
    if (action.includes("admin")) return "text-purple-600";
    if (action.includes("error")) return "text-red-600";
    return "text-gray-600";
  };

  const getStatusColor = (success: boolean) => {
    return success ? "text-green-600" : "text-red-600";
  };

  if (loading && !summary) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">
        Admin Logs Dashboard
      </h1>

      {/* Summary Section */}
      {summary && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            Activity Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {summary.summary.total_actions}
              </div>
              <div className="text-sm text-blue-500">Total Actions</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {summary.summary.successful_actions}
              </div>
              <div className="text-sm text-green-500">Successful</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {summary.summary.failed_actions}
              </div>
              <div className="text-sm text-red-500">Failed</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {summary.summary.success_rate.toFixed(1)}%
              </div>
              <div className="text-sm text-purple-500">Success Rate</div>
            </div>
          </div>

          {/* Action Breakdown */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-700 mb-3">
              Action Breakdown
            </h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.action_breakdown).map(
                ([action, count]) => (
                  <span
                    key={action}
                    className="bg-gray-100 px-3 py-1 rounded-full text-sm"
                  >
                    {action}: {count}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Filter by username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action
            </label>
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Filter by action"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Limit
            </label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleFilter}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Apply Filters
            </button>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleClearFilters}
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700">
            User Access Logs
          </h2>
          <p className="text-sm text-gray-500">Showing {logs.length} logs</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Endpoint
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Response Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {log.username || "Anonymous"}
                      </div>
                      {log.email && (
                        <div className="text-sm text-gray-500">{log.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${getActionColor(
                          log.action
                        )}`}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div
                        className="max-w-xs truncate"
                        title={log.endpoint || ""}
                      >
                        {log.method} {log.endpoint || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${getStatusColor(
                          log.success
                        )}`}
                      >
                        {log.success ? "Success" : "Failed"}
                      </span>
                      {log.status_code && (
                        <div className="text-xs text-gray-500">
                          ({log.status_code})
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.ip_address || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.response_time_ms
                        ? `${log.response_time_ms.toFixed(2)}ms`
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {logs.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            No logs found matching the current filters.
          </div>
        )}
      </div>

      {/* Note about pagination */}
      {logs.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Note: Pagination is limited due to database constraints. Use filters
          to narrow results.
        </div>
      )}
    </div>
  );
};

export default AdminLogs;
